import React from 'react';
import { useTranslation } from 'react-i18next';
import { goToDccLogin } from '../utils/dccLogin';
import { readAttemptedUrl } from '../utils/attemptedUrl';

/**
 * Shown when the session check comes back with no user.
 *
 * Rendered IN PLACE rather than redirecting. The app used to bounce straight to the DCC
 * login the moment `getUserData()` resolved null, but that check is two sequential network
 * calls (/csrf/token then /auth/me), so the layout chrome — sidebar, header, breadcrumb —
 * had already painted by the time it fired. The result read as "the page opened, then it
 * threw me to login": a flash of a screen the user was never allowed to see. Rendering a
 * definite answer instead means one transition, to one stable screen.
 *
 * Signing in is still one click away, and goToDccLogin carries ?next= so they come back
 * here afterwards.
 */
const NotAuthorized: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center"
      style={{ minHeight: '100vh', padding: '2rem', background: '#f7f9f8' }}
    >
      <div
        className="bg-white"
        style={{
          maxWidth: 480,
          width: '100%',
          padding: '2.5rem 2rem',
          borderRadius: 16,
          border: '1px solid #dbe5e1',
          borderLeft: '4px solid #1f7c50',
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center mx-auto"
          style={{ width: 56, height: 56, borderRadius: '50%', background: '#eaf4ef', marginBottom: '1.25rem' }}
          aria-hidden="true"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#1f7c50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h4 style={{ color: '#101413', marginBottom: '0.5rem' }}>
          {t('permissions.not_authorized_title') || 'Not authorized'}
        </h4>
        <p className="text-muted" style={{ marginBottom: '1.75rem' }}>
          {t('permissions.not_authorized_body') ||
            'You are not signed in, or your session has ended. Please sign in to continue.'}
        </p>

        <button
          type="button"
          onClick={() => goToDccLogin('not_authorized', { returnTo: readAttemptedUrl() })}
          className="btn"
          style={{
            background: '#1f7c50',
            color: '#fff',
            fontWeight: 600,
            padding: '0.65rem 1.5rem',
            borderRadius: 8,
            border: 'none',
            width: '100%',
          }}
        >
          {t('permissions.go_to_login') || 'Go to sign in'}
        </button>
      </div>
    </div>
  );
};

export default NotAuthorized;
